import React, { useMemo, useState } from 'react';
import { Card, Spinner, Alert, Badge } from 'react-bootstrap';
import { useListFscTrainingSignupsQuery } from '../../app/api';
import { brand, cardStyle, headStyle, EntPage, FilterBar } from './common';

/* Employee view of who signed up for which New Salesforce training session.
 * ("FSC" is retained in module, endpoint and table names — internal only.)
 * Any ENTREPRENEUR staff member can see it (the schedule config lives on the
 * ADMIN-gated Configuration page). Grouped by date → time slot → attendees. */

const fmtDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
const to12 = (hm) => { let [h, mi] = hm.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${String(mi).padStart(2, '0')} ${ap}`; };
const fmtSlot = (slot) => { const [a, b] = slot.split('-'); return `${to12(a)} – ${to12(b)}`; };
// Portal sign-ups name themselves through b2b_profiles; public ones (from the
// Salentica retirement page) carry their own optional name, which the API
// flattens into display_name. Email is the last resort — it is the only field a
// public sign-up is required to give.
const memberName = (r) => r.display_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email;

export default function FscTrainingSignups() {
  const { data = [], isLoading, isError } = useListFscTrainingSignupsQuery();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) => `${r.display_name || ''} ${r.first_name || ''} ${r.last_name || ''} ${r.email || ''} ${r.firm_name || ''}`.toLowerCase().includes(q));
  }, [data, search]);

  // date -> time -> [rows]
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((r) => {
      g[r.session_date] = g[r.session_date] || {};
      g[r.session_date][r.session_time] = g[r.session_date][r.session_time] || [];
      g[r.session_date][r.session_time].push(r);
    });
    return g;
  }, [filtered]);
  const dates = Object.keys(grouped).sort();

  return (
    <EntPage title="New Salesforce Training sign-ups" subtitle="Who's registered for each New Salesforce training session.">
      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span>Sessions &amp; attendees</span>
          <Badge bg="secondary" style={{ fontWeight: 600 }}>{filtered.length} sign-up{filtered.length === 1 ? '' : 's'}</Badge>
          <div style={{ marginLeft: 'auto' }}>
            <FilterBar search={search} onSearchChange={setSearch} placeholder="Search member or firm…" />
          </div>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center p-4"><Spinner size="sm" /></div>
          ) : isError ? (
            <Alert variant="danger" className="py-2">Could not load sign-ups.</Alert>
          ) : dates.length === 0 ? (
            <div style={{ color: brand.charcoal, fontSize: '0.88rem' }}>
              {data.length === 0 ? 'No one has signed up yet.' : 'No sign-ups match your search.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {dates.map((date) => (
                <div key={date}>
                  <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.95rem', marginBottom: '0.5rem', borderBottom: `2px solid ${brand.softGray}`, paddingBottom: '0.3rem' }}>
                    {fmtDate(date)}
                  </div>
                  {Object.keys(grouped[date]).sort().map((time) => {
                    const rows = grouped[date][time];
                    return (
                      <div key={time} style={{ marginBottom: '0.9rem' }}>
                        <div style={{ fontWeight: 600, color: brand.tealBlue, fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                          <i className="bi bi-clock me-2" />{fmtSlot(time)}
                          <Badge bg="light" text="dark" className="ms-2" style={{ fontWeight: 600 }}>{rows.length}</Badge>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', paddingLeft: '1.3rem' }}>
                          {rows.map((r, i) => (
                            <div key={`${r.email}-${i}`} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                              <span style={{ fontWeight: 600, color: brand.deepBlue }}>{memberName(r)}</span>
                              <span style={{ color: brand.charcoal }}>{r.email}</span>
                              {r.firm_name && <span style={{ color: brand.charcoal }}>· {r.firm_name}</span>}
                              {r.source === 'public' && (
                                /* No portal account yet — support@ was emailed to
                                   provision one, so this flags who to chase. */
                                <span style={{ background: brand.goldenrod, color: '#fff', borderRadius: 3,
                                               padding: '1px 6px', fontSize: '0.68rem', fontWeight: 700,
                                               textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  no login yet
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </EntPage>
  );
}
